import { useEffect, useState } from "react";
import { Autocomplete, Paper, PaperProps } from "@mui/material";
import Popper, { PopperProps } from "@mui/material/Popper";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { getTranslateByKey } from "@/main/services/utils";
import KogInputTextOutlined from "../inputText/inputTextOutlined";
import * as S from "./styles";

export interface ISelectMultipleOption {
	key: string | number;
	value: string;
}

interface ISelectMultipleOutlinedProps {
	id?: string;
	options: ISelectMultipleOption[];
	selected: ISelectMultipleOption[];
	onChange: (selected: ISelectMultipleOption[]) => void;
	onClearFirst?: () => void;
	placeholder?: string;
	disabled?: boolean;
}

const CustomPaper = (props: PaperProps) => (
	<Paper elevation={8} {...props} className={`${props.className} ignore-click`} />
);

const CustomPopper = (props: PopperProps) => (
	<Popper
		{...props}
		style={{
			...props.style,
			width: "auto",
			minWidth: props.style?.width as string,
		}}
	/>
);

function SelectMultipleOutlined({
	id,
	options,
	selected,
	onChange,
	onClearFirst,
	placeholder = "",
	disabled = false,
}: ISelectMultipleOutlinedProps) {
	const [isHovered, setIsHovered] = useState(false);
	const [inputValue, setInputValue] = useState("");

	const firstSelected = selected.length > 0 ? selected[0] : null;
	const selectedKeys = new Set(selected.map((s) => s.key));
	const visibleOptions = options.filter((opt) => !selectedKeys.has(opt.key));

	const showClearButton = isHovered && !!firstSelected && !!onClearFirst && !disabled;

	useEffect(() => {
		setInputValue(firstSelected?.value ?? "");
	}, [firstSelected?.key]);

	function handleSelect(optionSelected: ISelectMultipleOption) {
		const newSelected = firstSelected ? [firstSelected, ...selected.slice(1), optionSelected] : [optionSelected];

		onChange(newSelected);

		setInputValue(firstSelected?.value ?? optionSelected.value);
	}

	return (
		<S.AutocompleteContainer onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
			<Autocomplete
				id={id}
				size="small"
				disabled={disabled}
				disableClearable={!showClearButton}
				options={visibleOptions}
				noOptionsText={getTranslateByKey("information.noRecordsFound")}
				PaperComponent={CustomPaper}
				PopperComponent={CustomPopper}
				popupIcon={<KeyboardArrowDown />}
				getOptionKey={(option) => String(option.key)}
				getOptionLabel={(option) => option.value}
				isOptionEqualToValue={(option, val) => option.key === val.key}
				filterOptions={(opts, { inputValue: filter }) => {
					if (firstSelected && filter === firstSelected.value) return opts;
					if (!filter) return opts;

					return opts.filter((opt) => opt.value.toLowerCase().includes(filter.toLowerCase()));
				}}
				value={firstSelected ?? undefined}
				inputValue={inputValue}
				onInputChange={(_, newValue, reason) => {
					if (reason === "input") setInputValue(newValue);
				}}
				onChange={(_, optionSelected, reason) => {
					if (reason === "clear") {
						onClearFirst?.();
						return;
					}
					if (optionSelected) {
						handleSelect(optionSelected as ISelectMultipleOption);
					}
				}}
				onOpen={() => setInputValue(firstSelected?.value ?? "")}
				onClose={() => setInputValue(firstSelected?.value ?? "")}
				renderInput={(params) => (
					<KogInputTextOutlined
						{...params.InputProps}
						inputProps={params.inputProps}
						placeholder={placeholder}
						disabled={disabled}
					/>
				)}
			/>
		</S.AutocompleteContainer>
	);
}

export default SelectMultipleOutlined;
